import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  SeatingAssignmentInput,
  SeatingConstraint,
  SeatingPlan,
  SeatingStats,
  Table,
  Violation,
} from '@guabi/shared';
import { apiDelete, apiPatch, apiPost, apiGet } from '@/lib/api';

export const seatingKey = (wedId: string) => ['seating', wedId] as const;

export interface SeatingResponse {
  tables: Table[];
  constraints: SeatingConstraint[];
  stats: SeatingStats;
  violations: Violation[];
}

export function useSeating(wedId: string) {
  return useQuery({
    queryKey: seatingKey(wedId),
    queryFn: () => apiGet<SeatingResponse>(`/weddings/${wedId}/seating`),
  });
}

/** Seating changes move the guest list too — a seated guest is a guest row. */
function useSeatingMutation<TArgs, TResult = void>(
  fn: (args: TArgs) => Promise<TResult>,
  wedId: string,
) {
  const qc = useQueryClient();
  return useMutation<TResult, Error, TArgs>({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: seatingKey(wedId) });
      void qc.invalidateQueries({ queryKey: ['guests', wedId] });
    },
  });
}

export function useCreateTable(wedId: string) {
  return useSeatingMutation<{ name: string; capacity: number }, Table>(
    (input) => apiPost<Table>(`/weddings/${wedId}/seating/tables`, input),
    wedId,
  );
}

export function useUpdateTable(wedId: string) {
  return useSeatingMutation<
    { tableId: string; input: Partial<Table> & { updatedAt: string } },
    Table
  >(
    ({ tableId, input }) => apiPatch<Table>(`/weddings/${wedId}/seating/tables/${tableId}`, input),
    wedId,
  );
}

export function useDeleteTable(wedId: string) {
  return useSeatingMutation<{ tableId: string }>(
    ({ tableId }) => apiDelete(`/weddings/${wedId}/seating/tables/${tableId}`),
    wedId,
  );
}

export function useAssignSeats(wedId: string) {
  return useSeatingMutation<SeatingAssignmentInput, { moved: number }>(
    (input) => apiPost<{ moved: number }>(`/weddings/${wedId}/seating/assign`, input),
    wedId,
  );
}

export function useCreateConstraint(wedId: string) {
  return useSeatingMutation<{ kind: 'together' | 'apart'; guestIds: string[] }, SeatingConstraint>(
    (input) => apiPost<SeatingConstraint>(`/weddings/${wedId}/seating/constraints`, input),
    wedId,
  );
}

export function useDeleteConstraint(wedId: string) {
  return useSeatingMutation<{ constraintId: string }>(
    ({ constraintId }) => apiDelete(`/weddings/${wedId}/seating/constraints/${constraintId}`),
    wedId,
  );
}

/** Returns a proposal and writes nothing; applying it is a bulk assign. */
export function useProposeSeating(wedId: string) {
  return useMutation<SeatingPlan, Error, void>({
    mutationFn: () => apiPost<SeatingPlan>(`/weddings/${wedId}/seating/propose`, {}),
  });
}

/**
 * Moves a table on the plan.
 *
 * **Deliberately sends no `updatedAt`.** The service falls back to the row it
 * just read, so this is last-writer-wins — which is the right semantic for a
 * position: there is nothing to merge, and whoever dragged it last is right.
 * Sending a token would mean two quick drags 409 on the second, because the
 * first drag's response is what carries the new one.
 *
 * It writes the response into the cache instead of invalidating. A drag that
 * invalidates refetches the whole guest list and the table snaps back for a
 * frame before the new position arrives. The row written here is the server's
 * own answer, so it is exact, not optimistic. It also leaves `['guests']` alone:
 * moving a table changes nobody's seat.
 */
export function useMoveTable(wedId: string) {
  const qc = useQueryClient();
  return useMutation<Table, Error, { tableId: string; x: number; y: number }>({
    mutationFn: ({ tableId, x, y }) =>
      apiPatch<Table>(`/weddings/${wedId}/seating/tables/${tableId}`, { x, y }),
    onSuccess: (table) =>
      qc.setQueryData<SeatingResponse>(seatingKey(wedId), (old) =>
        old
          ? {
              ...old,
              tables: old.tables.map((t) => (t.tableId === table.tableId ? table : t)),
            }
          : old,
      ),
  });
}
